/**
 * @fileoverview Overall component for the Feature Selection launch step.
 *
 * @author Wyatt Lindquist (wlindquist@yext.com)
 */
import {useAjax} from '/src/com/yext/listingslaunchstorm/js/common/hooks/useajax';
import {ErrorCallout} from '/src/com/yext/listingslaunchstorm/js/enterprise/errorcallout/errorcallout';
import {EntitySelection} from '/src/com/yext/listingslaunchstorm/js/enterprise/selectentities/data/entityselection';
import {FeatureSelection} from '/src/com/yext/listingslaunchstorm/js/enterprise/selectfeatures/data/featureselection';
import {LicensePackOption} from '/src/com/yext/listingslaunchstorm/js/enterprise/selectfeatures/data/licensepackoption';
import {getLicensePacks} from '/src/com/yext/listingslaunchstorm/js/enterprise/selectfeatures/datasource/getlicensepacks';
import {SelectableCardList} from '/src/com/yext/listingslaunchstorm/js/enterprise/selectfeatures/selectablecardlist';
import {SkipSetup} from '/src/com/yext/listingslaunchstorm/js/enterprise/skipsetup/skipsetup';
import {CalloutBanner, CalloutBannerType} from '/ui/components/calloutbanner/calloutbanner';
import {Card} from '/ui/components/card/card';
import {ContentSkeleton} from '/ui/components/contentskeleton/contentskeleton';

import * as styles from '/src/com/yext/listingslaunchstorm/js/enterprise/selectfeatures/selectfeatures.module.scss';
import BinocularsSvg from '/src/com/yext/listingslaunchstorm/public/icons/binoculars.svg';

/**
 * @param {{
 *   selection: (FeatureSelection | undefined),
 *   onChangeSelection: function(FeatureSelection): void,
 *   entitySelection: EntitySelection,
 * }} props
 * @returns {React.ReactNode}
 */
export function SelectFeatures({
  selection,
  onChangeSelection,
  entitySelection,
}) {
  const {status, data, refetch} =
    useAjax(
      true,
      getLicensePacks,
      entitySelection.savedFilterId);

  const noSelection = (selection === undefined);
  // By default, once the license packs are available, we want to select all available options.
  React.useEffect(() => {
    if (noSelection && data) {
      const ids = data.options
        .filter(option => isLicensePackAvailable(option))
        .map(option => option.id);
      onChangeSelection(featureSelectionFromLicensePackIds(ids, data.options, false /* skipSetup */));
    }
  }, [noSelection, data, entitySelection, onChangeSelection]);

  /**
   * @param {boolean} skipSetup
   */
  function setSkipFeatureSelection(skipSetup) {
    const ids = selection?.ids;
    const displayNames = selection?.displayNames;

    onChangeSelection(
      new FeatureSelection({
        ids: ids ?? [],
        displayNames: displayNames ?? [],
        skipSetup,
      }));
  }

  if (status === 'error') {
    return (<ErrorCallout onReload={refetch} />);
  } else if (status !== 'success') {
    return (
      <div className={styles.page}>
        {[...Array(4)].map((_v, i) => (
          <ContentSkeleton key={i} type="box" height={150} />
        ))}
      </div>
    );
  } else {
    return (
      <>
        <div className={styles.page}>
          <OverwriteWarningCallout />
          {
            data
            && data.options
            && data.options.length === 0
            && <EmptyFeatureCard />
          }
          <SelectableCardList
            selected={!selection?.skipSetup ? selection?.ids || [] : []}
            onSelected={values => onChangeSelection(
              featureSelectionFromLicensePackIds(values, data.options, false /* skipSetup */),
            )}
          >
            {data?.options?.map(option =>
              <SelectableCardList.Card
                key={option.id}
                title={option.name}
                value={option.id}
                disabled={!isLicensePackAvailable(option) || selection?.skipSetup}
              >
                <LicensePackCardContent
                  licensePack={option}
                  entitySelection={entitySelection}
                />
              </SelectableCardList.Card>,
            )}
          </SelectableCardList>
        </div>
        <SkipSetup
          skipSetup={selection?.skipSetup}
          disabled={false}
          onClick={() => {
            setSkipFeatureSelection(!selection?.skipSetup);
          }}
          tooltipText={yext.msg('By skipping this step, your entities will not be opted in to the Publisher network, which is essential for full capabilities. You can change this later under Account Settings > License Packs.')}
        />
      </>
    );
  }
}

/**
 * @param {{
 *   licensePack: LicensePackOption,
 *   entitySelection: EntitySelection,
 * }} props
 * @returns {React.ReactNode}
 */
function LicensePackCardContent({
  licensePack,
  entitySelection,
}) {
  const required = licensePack.neededCount;
  const available = licensePack.availableCount;
  const needsMore = required > available;

  return (
    <>
      <div className={styles.heroNumbers}>
        <HeroNumber count={entitySelection.entityCount} description={yext.msg('Entities Selected')} />
        <HeroNumber count={available} description={yext.msg('Licenses Available')} />
        <HeroNumber
          count={licensePack.neededCount}
          description={<b>{yext.msg('Licenses Needed')}</b>}
        />
      </div>
      {needsMore
        ? <MoreLicensesCallout />
        : <LicenseAssignmentMessage savedFilterName={entitySelection.displayName} />
      }
    </>
  );
}

/**
 * @param {{
 *   count: number,
 *   description: React.ReactNode,
 * }} props
 * @returns {React.ReactNode}
 */
function HeroNumber({
  count,
  description,
}) {
  return (
    <div>
      <div className={styles.number}>{count}</div>
      <div className={styles.description}>{description}</div>
    </div>
  );
}

/** @returns {React.ReactNode} */
function OverwriteWarningCallout() {
  const callout = yext.msg('Any changes from the default settings will overwrite the default <b>account-level</b> settings.');

  return (
    <CalloutBanner type={CalloutBannerType.WARNING}>
      <span dangerouslySetInnerHTML={{__html: callout}} />
    </CalloutBanner>
  );
}

/** @returns {React.ReactNode} */
function EmptyFeatureCard() {
  return (
    <Card>
      <Card.Body>
        <div className={styles.emptyFeatureContent}>
          <div className={styles.svgContainer}>
            <BinocularsSvg/>
          </div>
          <div className={styles.cta}>
            <h3>{yext.msg('No Features Found')}</h3>
            <div>
              {yext.msg('You don’t have any listings licenses. Send an email to your Account Manager to purchase some licenses and use ‘Skip Setup’ to continue for now.')}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

/**
 * @param {{
 *   savedFilterName: string,
 * }} props
 * @returns {React.ReactNode}
 */
function LicenseAssignmentMessage({savedFilterName}) {
  const message = yext.msg('Automatic License Assignment is <b>on</b> using your <b>{0}</b> saved filter', savedFilterName);

  return (
    <div>
      <p dangerouslySetInnerHTML={{__html: message}} />
      <p>
        {yext.msg('This will automatically assign licenses to entities that meet the filter\'s criteria, including entities that are added to the filter later. Entities that are removed from the filter will automatically have their licenses removed.')}
      </p>
    </div>
  );
}

/** @returns {React.ReactNode} */
function MoreLicensesCallout() {
  return (
    <CalloutBanner type={CalloutBannerType.ERROR}>
      <span>
        {yext.msg('There are not enough licenses for the selected locations. Please send an email to your Account Manager to purchase more licenses or adjust the number of entities in your Saved Filter.')}
      </span>
    </CalloutBanner>
  );
}

/**
 * @param {!Array<number>} ids
 * @param {Array<LicensePackOption>} options
 * @param {boolean} skipSetup
 * @returns {FeatureSelection}
 */
function featureSelectionFromLicensePackIds(ids, options, skipSetup) {
  return new FeatureSelection({
    ids,
    displayNames: options.filter(opt => ids.includes(opt.id)).map(opt => opt.name),
    skipSetup,
  });
}

/**
 * @param {LicensePackOption} option
 * @returns {boolean}
 */
function isLicensePackAvailable(option) {
  return (option.availableCount >= option.neededCount);
}
